import { useState, useEffect } from 'react'
import axios from 'axios'
import { Activity, Server, ShieldAlert, ShieldCheck, RefreshCw, AlertTriangle, FileText } from 'lucide-react'
import { KnowledgeTab } from './KnowledgeTab'

const API_BASE = import.meta.env.VITE_RAG_API_URL || 'http://localhost:8000'

type TabId = 'dashboard' | 'knowledge'



interface Anomaly {
    id: string
    type: string
    severity: string
    description: string
    timestamp: string
}

interface PolicyEvent {
    policy_name: string
    risk_score: number
    impact_rate_5m: number
    current_state?: string
    last_transition_at: string
}

export function OperationsCenter() {
    // State
    const [metrics, setMetrics] = useState<any>(null)
    const [criticalPolicies, setCriticalPolicies] = useState<PolicyEvent[]>([])
    const [blockedPolicies, setBlockedPolicies] = useState<PolicyEvent[]>([])

    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

    const [refreshInterval, setRefreshInterval] = useState<number>(0) // 0 = off, 5000 = 5s
    const [activeTab, setActiveTab] = useState<TabId>('dashboard')

    // API Calls
    const fetchData = async () => {
        setIsLoading(true)
        setError(null)
        try {
            // Parallel Fetch
            const [metricsRes, criticalRes, blockedRes] = await Promise.all([
                axios.get(`${API_BASE} /ops/metrics`),
                axios.get(`${API_BASE} /ops/policy / critical`),
                axios.get(`${API_BASE} /ops/policy / blocked`)
            ])

            setMetrics(metricsRes.data)
            setCriticalPolicies(criticalRes.data.critical_policies || [])
            setBlockedPolicies(blockedRes.data.blocked_policies || [])
            setLastUpdated(new Date())

        } catch (err: any) {
            console.warn('Ops fetch failed:', err.message)
            setError('Falha na conexão com servidor (Offline)')
        } finally {
            setIsLoading(false)
        }
    }

    // Effect for Auto-Refresh
    useEffect(() => {
        if (activeTab === 'dashboard') {
            fetchData() // Initial load

            if (refreshInterval > 0) {
                const timer = setInterval(fetchData, refreshInterval)
                return () => clearInterval(timer)
            }
        }
    }, [refreshInterval, activeTab])

    return (
        <div className="max-w-[1600px] mx-auto">
            {/* Tab Navigation */}
            <div className="flex gap-2 mb-6 border-b border-white/10">
                <button
                    onClick={() => setActiveTab('dashboard')}
                    className={`px - 4 py - 2 text - sm font - medium transition - all ${activeTab === 'dashboard'
                        ? 'text-white border-b-2 border-blue-500'
                        : 'text-gray-400 hover:text-gray-300'
                        } `}
                >
                    <Activity className="w-4 h-4 inline mr-2" />
                    Dashboard
                </button>
                <button
                    onClick={() => setActiveTab('knowledge')}
                    className={`px - 4 py - 2 text - sm font - medium transition - all ${activeTab === 'knowledge'
                        ? 'text-white border-b-2 border-blue-500'
                        : 'text-gray-400 hover:text-gray-300'
                        } `}
                >
                    <FileText className="w-4 h-4 inline mr-2" />
                    Base de Conhecimento
                </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'knowledge' ? (
                <KnowledgeTab />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[calc(100vh-200px)] overflow-hidden">

                    {/* LEFT COLUMN: System Health */}
                    <div className="flex flex-col gap-6 overflow-hidden">
                        {/* Header Card */}
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 shrink-0">
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                                        <Activity className="w-5 h-5 text-blue-400" />
                                        System Health
                                    </h2>
                                    <p className="text-xs text-gray-400 mt-1">
                                        Last updated: {lastUpdated ? lastUpdated.toLocaleTimeString() : '--'}
                                    </p>
                                </div>

                                <div className="flex items-center gap-2">
                                    <select
                                        value={refreshInterval}
                                        onChange={(e) => setRefreshInterval(Number(e.target.value))}
                                        className="bg-black/40 border border-white/10 text-xs text-gray-300 rounded px-2 py-1.5 focus:outline-none"
                                    >
                                        <option value={0}>Manual Refresh</option>
                                        <option value={5000}>Every 5s</option>
                                        <option value={15000}>Every 15s</option>
                                        <option value={30000}>Every 30s</option>
                                    </select>

                                    <button
                                        onClick={fetchData}
                                        disabled={isLoading}
                                        className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 text-white rounded-lg transition-colors"
                                    >
                                        <RefreshCw className={`w - 4 h - 4 ${isLoading ? 'animate-spin' : ''} `} />
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-black/20 border border-white/5 rounded-lg">
                                    <span className="text-xs text-gray-500 uppercase tracking-wider">Status</span>
                                    <div className="flex items-center gap-2 mt-1">
                                        <div className={`w - 3 h - 3 rounded - full ${error ? 'bg-red-500' : 'bg-green-500'} `}></div>
                                        <span className={`font - mono text - lg ${error ? 'text-red-400' : 'text-green-400'} `}>
                                            {error ? 'OFFLINE' : (metrics ? 'OPERATIONAL' : 'Connecting...')}
                                        </span>
                                    </div>
                                </div>

                                <div className="p-4 bg-black/20 border border-white/5 rounded-lg">
                                    <span className="text-xs text-gray-500 uppercase tracking-wider">Active Workers</span>
                                    <div className="flex items-center gap-2 mt-1">
                                        <Server className="w-4 h-4 text-purple-400" />
                                        <span className="font-mono text-lg text-white">
                                            {metrics?.workers?.length || 0}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Anomalies List */}
                        <div className="flex-1 bg-white/5 border border-white/10 rounded-2xl p-6 overflow-hidden flex flex-col">
                            <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                                Anomalies Detected
                            </h3>

                            <div className="flex-1 overflow-auto custom-scrollbar space-y-2">
                                {metrics?.anomalies?.length > 0 ? (
                                    metrics.anomalies.map((anomaly: Anomaly, idx: number) => (
                                        <div key={idx} className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex gap-3">
                                            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-sm text-red-200 font-medium">{anomaly.severity} - {anomaly.type}</p>
                                                <p className="text-xs text-red-300/70 mt-1">{anomaly.description}</p>
                                                <p className="text-[10px] text-gray-500 mt-2 font-mono">{anomaly.timestamp}</p>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="h-full flex flex-col items-center justify-center text-gray-600">
                                        <ShieldCheck className="w-12 h-12 opacity-20 mb-2" />
                                        <p className="text-sm">No anomalies detected</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: Policy Governance */}
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 overflow-hidden flex flex-col">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                                <ShieldAlert className="w-5 h-5 text-orange-400" />
                                Policy Governance
                            </h2>
                        </div>

                        <div className="flex-1 overflow-auto custom-scrollbar space-y-6">
                            {/* Critical Policies */}
                            <div>
                                <h3 className="text-sm font-medium text-red-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    Critical State (Kill Switch)
                                    <span className="px-1.5 py-0.5 bg-red-500/20 rounded text-xs text-red-300">{criticalPolicies.length}</span>
                                </h3>
                                {criticalPolicies.length > 0 ? (
                                    <div className="space-y-2">
                                        {criticalPolicies.map((policy) => (
                                            <div key={policy.policy_name} className="p-3 bg-red-900/20 border border-red-500/50 rounded-lg">
                                                <div className="flex justify-between items-start">
                                                    <span className="font-mono text-red-200 font-semibold">{policy.policy_name}</span>
                                                    <span className="text-xs text-red-400 font-mono">RISK: {policy.risk_score}</span>
                                                </div>
                                                <div className="mt-2 grid grid-cols-2 gap-4 text-xs">
                                                    <div>
                                                        <span className="text-gray-500 block">Impact Rate (5m)</span>
                                                        <span className="text-white font-mono">{(policy.impact_rate_5m * 100).toFixed(1)}%</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500 block">Last Transition</span>
                                                        <span className="text-white font-mono">{new Date(policy.last_transition_at).toLocaleTimeString()}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-600 italic px-4">No critical policies active.</p>
                                )}
                            </div>

                            {/* Blocked Policies (High Risk) */}
                            <div>
                                <h3 className="text-sm font-medium text-orange-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    Blocked Rollouts (High Risk)
                                    <span className="px-1.5 py-0.5 bg-orange-500/20 rounded text-xs text-orange-300">{blockedPolicies.length}</span>
                                </h3>
                                {blockedPolicies.length > 0 ? (
                                    <div className="space-y-2">
                                        {blockedPolicies.map((policy) => (
                                            <div key={policy.policy_name} className="p-3 bg-orange-900/10 border border-orange-500/30 rounded-lg">
                                                <div className="flex justify-between items-start">
                                                    <span className="font-mono text-orange-200 font-medium">{policy.policy_name}</span>
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-300 border border-orange-500/20">
                                                        {policy.current_state}
                                                    </span>
                                                </div>
                                                <div className="mt-2 text-xs">
                                                    <span className="text-gray-500">Risk Score: </span>
                                                    <span className="text-white font-mono">{policy.risk_score}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-600 italic px-4">No blocked rollouts.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
